import { call, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  CustomerUploadBusinessInformation,
  CustomerUploadInsurance,
  CustomerUploadBusinessDetails,
  FetchCustomerProfile,
  FetchUser,
  CustomerUploadUpdatedInsurance,
  CustomerUploadUpdatedBusinessDetails,
  CustomerUploadUpdatedTSA,
  CustomerUploadTSA,
  CustomerUploadBankInformation,
} from 'src/constants/actionTypes'
import { ApiService, callSecureApi } from '../api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import {
  PaymentMethodRecipientNamePreference,
  PaymentMethodTypeEnum,
  RecipientTypeEnum,
} from 'src/interfaces/paymentMethod'

function* onFetchCustomerProfile(action: AsyncAction<undefined, any>) {
  try {
    const { payload }: { payload: any } = yield call(
      callSecureApi,
      Endpoints.FetchCustomerProfile,
      {
        apiService: ApiService.Paraworks,
      },
    )

    yield put(FetchCustomerProfile.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch customer onboarding state'

    yield put(FetchCustomerProfile.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CustomerUploadBusinessInformationRequest {
  companyName?: string
  businessType?: string
  dotNumber?: string
  addressStreet1?: string
  addressStreet2?: string
  addressCity?: string
  addressState?: string
  addressPostalCode?: string
  taxIdType?: string
  ssn?: string
  ein?: string
  itin?: string
}

function* onCustomerUploadBusinessInformation(
  action: AsyncAction<CustomerUploadBusinessInformationRequest>,
) {
  const {
    companyName,
    businessType,
    dotNumber,
    addressStreet1,
    addressStreet2,
    addressCity,
    addressState,
    addressPostalCode,
    taxIdType,
    ssn,
    ein,
    itin,
  } = action.payload

  try {
    yield call(callSecureApi, Endpoints.CustomerUploadBusinessInformation, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        companyName,
        businessType,
        dotNumber,
        addressStreet1,
        addressStreet2,
        addressCity,
        addressState,
        addressPostalCode,
        taxIdType,
        ssn,
        ein,
        itin,
      },
    })

    yield put(CustomerUploadBusinessInformation.success())
    yield put(FetchUser.request())
    yield put(FetchCustomerProfile.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload business information'

    yield put(CustomerUploadBusinessInformation.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CustomerUploadInsuranceRequest {
  collectedDataId: string
  file: string
}

function* onCustomerUploadInsurance(action: AsyncAction<CustomerUploadInsuranceRequest>) {
  const { collectedDataId, file } = action.payload

  try {
    const formData = new FormData()

    formData.append('collectedDataId', collectedDataId)
    formData.append('file', file)

    yield call(callSecureApi, Endpoints.CustomerUploadInsurance, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(CustomerUploadInsurance.success())
    yield put(FetchUser.request())
    yield put(FetchCustomerProfile.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload insurance'

    yield put(CustomerUploadInsurance.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onCustomerUploadUpdatedInsurance(action: AsyncAction<CustomerUploadInsuranceRequest>) {
  const { collectedDataId, file } = action.payload

  try {
    const formData = new FormData()

    formData.append('collectedDataId', collectedDataId)
    formData.append('file', file)

    yield call(callSecureApi, Endpoints.CustomerUploadUpdatedInsurance, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: formData,
      resetContentType: true,
      returnResponse: true,
    })

    yield put(CustomerUploadUpdatedInsurance.success())
    yield put(FetchUser.request())
    yield put(FetchCustomerProfile.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload insurance'

    yield put(CustomerUploadUpdatedInsurance.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CustomerUploadUpdatedBusinessDetailsRequest {
  doesBusinessAdvertise: boolean
  hasQualityProgram: boolean
  qualityProgramDetails?: string
  vehicleAccessDetails?: string
  coveredCitiesDetails?: string
}

interface CustomerUploadBusinessDetailsRequest extends CustomerUploadUpdatedBusinessDetailsRequest {
  collectedDataId: string
}

function* onCustomerUploadBusinessDetails(
  action: AsyncAction<CustomerUploadBusinessDetailsRequest>,
) {
  const {
    collectedDataId,
    doesBusinessAdvertise,
    hasQualityProgram,
    qualityProgramDetails,
    vehicleAccessDetails,
    coveredCitiesDetails,
  } = action.payload

  try {
    yield call(callSecureApi, Endpoints.CustomerUploadBusinessDetails, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        collectedDataId,
        doesBusinessAdvertise,
        hasQualityProgram,
        qualityProgramDetails,
        vehicleAccessDetails,
        coveredCitiesDetails,
      },
    })

    yield put(CustomerUploadBusinessDetails.success())
    yield put(FetchUser.request())
    yield put(FetchCustomerProfile.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload business details'

    yield put(CustomerUploadBusinessDetails.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onCustomerUploadUpdatedBusinessDetails(
  action: AsyncAction<CustomerUploadUpdatedBusinessDetailsRequest>,
) {
  const {
    doesBusinessAdvertise,
    hasQualityProgram,
    qualityProgramDetails,
    vehicleAccessDetails,
    coveredCitiesDetails,
  } = action.payload

  try {
    yield call(callSecureApi, Endpoints.CustomerUploadUpdatedBusinessDetails, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        doesBusinessAdvertise,
        hasQualityProgram,
        qualityProgramDetails,
        vehicleAccessDetails,
        coveredCitiesDetails,
      },
    })

    yield put(CustomerUploadUpdatedBusinessDetails.success())
    yield put(FetchUser.request())
    yield put(FetchCustomerProfile.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload business details'

    yield put(CustomerUploadUpdatedBusinessDetails.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CustomerUploadUpdatedTSARequest {
  isRegisteredAgent: boolean
  iacNumber?: string
  dateOfLastCertification?: string
  agentName?: string
  coordinatorName?: string
  coordinatorPhoneNumber?: string
  coordinatorEmail?: string
  hasTwic?: boolean
  twicContractorsNumber?: number
  hasFast?: boolean
  fastContractorsNumber?: number
  hasSida?: boolean
  sidaContractorsNumber?: number
  hasHazmat?: boolean
  hazmatContractorsNumber?: number
}

interface CustomerUploadTSARequest extends CustomerUploadUpdatedTSARequest {
  collectedDataId: string
}

function* onCustomerUploadTSA(action: AsyncAction<CustomerUploadTSARequest>) {
  const {
    collectedDataId,
    isRegisteredAgent,
    iacNumber,
    dateOfLastCertification,
    agentName,
    coordinatorName,
    coordinatorPhoneNumber,
    coordinatorEmail,
    hasTwic,
    twicContractorsNumber,
    hasFast,
    fastContractorsNumber,
    hasSida,
    sidaContractorsNumber,
    hasHazmat,
    hazmatContractorsNumber,
  } = action.payload

  try {
    yield call(callSecureApi, Endpoints.CustomerUploadTSA, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        collectedDataId,
        isRegisteredAgent,
        iacNumber,
        dateOfLastCertification,
        agentName,
        coordinatorName,
        coordinatorPhoneNumber,
        coordinatorEmail,
        hasTwic,
        twicContractorsNumber,
        hasFast,
        fastContractorsNumber,
        hasSida,
        sidaContractorsNumber,
        hasHazmat,
        hazmatContractorsNumber,
      },
    })

    yield put(CustomerUploadTSA.success())
    yield put(FetchUser.request())
    yield put(FetchCustomerProfile.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload business details'

    yield put(CustomerUploadTSA.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onCustomerUploadUpdatedTSA(action: AsyncAction<CustomerUploadUpdatedTSARequest>) {
  const {
    isRegisteredAgent,
    iacNumber,
    dateOfLastCertification,
    agentName,
    coordinatorName,
    coordinatorPhoneNumber,
    coordinatorEmail,
    hasTwic,
    twicContractorsNumber,
    hasFast,
    fastContractorsNumber,
    hasSida,
    sidaContractorsNumber,
    hasHazmat,
    hazmatContractorsNumber,
  } = action.payload

  try {
    yield call(callSecureApi, Endpoints.CustomerUploadUpdatedTSA, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      body: {
        isRegisteredAgent,
        iacNumber,
        dateOfLastCertification,
        agentName,
        coordinatorName,
        coordinatorPhoneNumber,
        coordinatorEmail,
        hasTwic,
        twicContractorsNumber,
        hasFast,
        fastContractorsNumber,
        hasSida,
        sidaContractorsNumber,
        hasHazmat,
        hazmatContractorsNumber,
      },
    })

    yield put(CustomerUploadUpdatedTSA.success())
    yield put(FetchUser.request())
    yield put(FetchCustomerProfile.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload business details'

    yield put(CustomerUploadUpdatedTSA.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface CustomerUploadBankInformationRequest {
  routingNumber: string
  accountNumber: string
  accountType: string
}

function* onCustomerUploadBankInformation(
  action: AsyncAction<CustomerUploadBankInformationRequest>,
) {
  const { routingNumber, accountNumber, accountType } = action.payload

  try {
    yield call(callSecureApi, Endpoints.CustomerUploadPaymentMethod, {
      method: 'POST',
      body: {
        paymentMethodType: PaymentMethodTypeEnum.BankAccount,
        recipientNamePreference: PaymentMethodRecipientNamePreference.BusinessName,
        bankAccountDetails: {
          recipientType: RecipientTypeEnum.Business,
          routingNumber,
          accountNumber,
          accountType,
        },
      },
      apiService: ApiService.Paraworks,
    })

    yield put(CustomerUploadBankInformation.success())

    yield put(FetchUser.request())
    yield put(FetchCustomerProfile.request())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to upload bank information'

    yield put(CustomerUploadBankInformation.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* customerStepsSaga() {
  yield takeEvery(FetchCustomerProfile.type.REQUEST, onFetchCustomerProfile)
  yield takeEvery(
    CustomerUploadBusinessInformation.type.REQUEST,
    onCustomerUploadBusinessInformation,
  )
  yield takeEvery(CustomerUploadInsurance.type.REQUEST, onCustomerUploadInsurance)
  yield takeEvery(CustomerUploadUpdatedInsurance.type.REQUEST, onCustomerUploadUpdatedInsurance)
  yield takeEvery(CustomerUploadBusinessDetails.type.REQUEST, onCustomerUploadBusinessDetails)
  yield takeEvery(
    CustomerUploadUpdatedBusinessDetails.type.REQUEST,
    onCustomerUploadUpdatedBusinessDetails,
  )
  yield takeEvery(CustomerUploadTSA.type.REQUEST, onCustomerUploadTSA)
  yield takeEvery(CustomerUploadUpdatedTSA.type.REQUEST, onCustomerUploadUpdatedTSA)
  yield takeEvery(CustomerUploadBankInformation.type.REQUEST, onCustomerUploadBankInformation)
}
